/**
 * Reports data layer: API types, URL <-> report-params mapping and labels. Pages and dialogs import from here so the
 * presentation (FilterToolbar, Select, Dialog, DataTable) can be swapped for the shared design system without
 * touching data code.
 */
import type { ConsoleRole } from "@flexfreight/core";

export type ColKind = "text" | "number" | "money" | "percent";
export type ReportColumn = { key: string; label: string; kind: ColKind };
export type ReportGroup = "day" | "week" | "month";
export type ReportFilter = "carrier" | "customer" | "queue";
export const RANGE_PRESETS = ["last_7_days", "last_30_days", "last_90_days", "month_to_date", "last_month", "year_to_date", "last_12_months", "custom"] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];
export const RANGE_LABEL: Record<RangePreset, string> = {
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
  month_to_date: "Month to date",
  last_month: "Last month",
  year_to_date: "Year to date",
  last_12_months: "Last 12 months",
  custom: "Custom dates",
};
export const GROUP_LABEL: Record<ReportGroup, string> = { day: "Day", week: "Week", month: "Month" };
export const QUEUES = ["support", "billing", "claims", "sales", "carrier"] as const;

export type ReportData = {
  id: string;
  title: string;
  description: string;
  from: string;
  to: string;
  range: RangePreset;
  group: ReportGroup | null;
  groups: ReportGroup[];
  filters: ReportFilter[];
  columns: ReportColumn[];
  allColumns: ReportColumn[];
  rows: Record<string, string | number | null>[];
  showsCost: boolean;
};
export type ReportMeta = { id: string; title: string; description: string; status: "ready" | "integration_required" };

/** What a saved view stores (mirrors the API's ReportParamsSchema). */
export type ReportParams = {
  range?: RangePreset;
  from?: string;
  to?: string;
  group?: ReportGroup;
  carrier?: string;
  customer?: string;
  customerName?: string;
  queue?: string;
  cols?: string[];
};
export type SavedView = {
  id: string;
  name: string;
  reportId: string;
  reportTitle: string;
  visibility: "personal" | "role";
  sharedRole: ConsoleRole | null;
  config: ReportParams;
  owner: { id: string; name: string | null; isMe: boolean };
  canEdit: boolean;
  schedules: number;
  updatedAt: string;
};
export type ScheduleState = "active" | "paused" | "waiting_for_email" | "failing";
export type Schedule = {
  id: string;
  viewId: string;
  viewName: string | null;
  reportId: string | null;
  frequency: "daily" | "weekly" | "monthly";
  hour: number;
  weekday: number | null;
  monthDay: number | null;
  timezone: string;
  delivery: "csv" | "link";
  recipients: string[];
  state: ScheduleState;
  stateLabel: string;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  createdBy: { id: string | null; name: string | null; isMe: boolean };
  canManage: boolean;
};
export type SchedulesResponse = { emailConnected: boolean; scope: "all" | "mine"; schedules: Schedule[] };
export type Recipient = { email: string; name: string; role: ConsoleRole; seesCost: boolean };

export type Overview = {
  asOf: string;
  window: { from: string; to: string; timezone: string };
  showsCost: boolean;
  revenueTrend: { from: string; to: string; months: { month: string; shipments: number; revenueCents: number; costCents?: number; marginCents?: number; marginPct?: number | null }[] };
  funnel: { steps: { key: string; label: string; value: number | null; source: string; note?: string; pctOfPrev?: number | null }[] };
  carrierOnTime: { carrier: string; name: string; shipments: number; delivered: number; measured: number; onTime: number; onTimePct: number | null }[];
  ticketSla: {
    queues: { queue: string; opened: number; firstResponse: { met: number; late: number; pct: number | null }; resolution: { met: number; late: number; pct: number | null } }[];
    overall: { opened: number; firstResponsePct: number | null; resolutionPct: number | null; firstResponseLate: number; resolutionLate: number };
  };
  unavailable: { metric: string; reason: string }[];
};

const PARAM_KEYS = ["range", "from", "to", "group", "carrier", "customer", "customerName", "queue"] as const;

/** Report params from the page URL. */
export function paramsFromUrl(sp: URLSearchParams): ReportParams {
  const p: ReportParams = {};
  for (const k of PARAM_KEYS) {
    const v = sp.get(k);
    if (v) (p as Record<string, string>)[k] = v;
  }
  const cols = sp.get("cols");
  if (cols) p.cols = cols.split(",").filter(Boolean);
  if (p.range && !(RANGE_PRESETS as readonly string[]).includes(p.range)) delete p.range;
  return p;
}

/** URL search params for report params (plus optional view id). */
export function urlFromParams(p: ReportParams, viewId?: string | null): URLSearchParams {
  const sp = new URLSearchParams();
  for (const k of PARAM_KEYS) {
    const v = p[k];
    if (v) sp.set(k, String(v));
  }
  if (p.cols?.length) sp.set("cols", p.cols.join(","));
  if (viewId) sp.set("view", viewId);
  return sp;
}

/** The API query for a run: only what the server reads (no display-only customerName, no view id). */
export function apiQuery(p: ReportParams, extra: Record<string, string> = {}): string {
  const sp = urlFromParams(p);
  sp.delete("customerName");
  for (const [k, v] of Object.entries(extra)) sp.set(k, v);
  return sp.toString();
}

/** A comparable form of params, to tell whether the page still matches a saved view. */
export function sameParams(a: ReportParams, b: ReportParams): boolean {
  const norm = (p: ReportParams) => {
    const range = p.range ?? (p.from || p.to ? "custom" : "last_90_days");
    return JSON.stringify({ range, from: range === "custom" ? (p.from ?? "") : "", to: range === "custom" ? (p.to ?? "") : "", group: p.group ?? "", carrier: p.carrier ?? "", customer: p.customer ?? "", queue: p.queue ?? "", cols: [...(p.cols ?? [])].sort().join(",") });
  };
  return norm(a) === norm(b);
}

export const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
export const compactUsd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const num = new Intl.NumberFormat("en-US");
export const fmtCell = (c: ReportColumn, v: string | number | null) => (v === null ? "-" : c.kind === "money" ? usd.format(Number(v) / 100) : c.kind === "percent" ? `${v}%` : c.kind === "number" ? num.format(Number(v)) : String(v));

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const WEEKDAY_OPTIONS = WEEKDAYS.map((label, i) => ({ value: String(i), label }));
export const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;
export const MONTHDAY_OPTIONS = Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: `The ${ordinal(i + 1)}` }));
export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${hourLabel(h)} Eastern` }));

/** "Every Monday at 7:00 AM Eastern". */
export function describeSchedule(s: Pick<Schedule, "frequency" | "hour" | "weekday" | "monthDay">): string {
  const at = `at ${hourLabel(s.hour)} Eastern`;
  if (s.frequency === "daily") return `Every day ${at}`;
  if (s.frequency === "weekly") return `Every ${WEEKDAYS[s.weekday ?? 1]} ${at}`;
  return `Monthly on the ${ordinal(s.monthDay ?? 1)} ${at}`;
}

/** "Month to date · Day · XPO · Acme" - what a view filters, for lists. */
export function describeParams(p: ReportParams, carrierName: (c: string) => string): string {
  const range = p.range ?? (p.from || p.to ? "custom" : "last_90_days");
  return [
    range === "custom" ? `${p.from ?? "…"} to ${p.to ?? "today"}` : RANGE_LABEL[range],
    p.group ? `By ${GROUP_LABEL[p.group].toLowerCase()}` : null,
    p.carrier ? carrierName(p.carrier) : null,
    p.customerName ?? (p.customer ? "One customer" : null),
    p.queue ? `${p.queue[0]!.toUpperCase()}${p.queue.slice(1)} queue` : null,
    p.cols?.length ? `${p.cols.length} columns` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export const ROLE_LABEL: Record<ConsoleRole, string> = { owner: "Owners", admin: "Admins", ops: "Operations", sales: "Sales", marketing: "Marketing", read_only: "Read-only" };
